/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useEffect, useState } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import {
  ConsumptionSourceName,
  MaterialGroupingName,
  MaterialGroupName,
  UnitType,
} from "src/store/api/generatedApi";
import {
  buildMaterialColours,
  defaultLayout,
  fractionTickFormatting,
  generalTickFormatting,
  getLineDash,
  getMarkerSymbol,
  getMaterialColour,
  setSelectedDateFromPlotClick,
} from "../plotting";
import {
  ConsumptionPlotType,
  DataLabelVisibility,
  GroupedConsumptionSeries,
} from "../types";
import { TFunction } from "i18next";
import { sorted } from "helpers";

interface ConsumptionPlotProps {
  consumptionSeries: GroupedConsumptionSeries;
  selectedGrouping: MaterialGroupingName | null;
  selectedProductGroup: string | null;
  selectedPlotType: ConsumptionPlotType;
  selectedConsumptionSources: ConsumptionSourceName[];
  selectedMaterialGroups: MaterialGroupName[];
  dataLabelVisibility: DataLabelVisibility;
  setSelectedDate: (date: Date) => void;
}

export const ConsumptionPlot = ({
  consumptionSeries,
  selectedGrouping,
  selectedProductGroup,
  selectedPlotType,
  selectedConsumptionSources,
  selectedMaterialGroups,
  dataLabelVisibility,
  setSelectedDate,
}: ConsumptionPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);

  const [figure, setFigure] = useState(() => ({
    data: buildInitialFigData(consumptionSeries, dataLabelVisibility, t),
    layout: updateLayout(defaultLayout, selectedPlotType, t, units),
  }));

  useEffect(() => {
    const newData = buildInitialFigData(
      consumptionSeries,
      dataLabelVisibility,
      t
    );
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- merges previous layout state; not purely computed
    setFigure({
      data: updateFigData(
        newData,
        selectedGrouping,
        selectedProductGroup,
        selectedPlotType,
        selectedConsumptionSources,
        selectedMaterialGroups
      ),
      layout: updateLayout(figure.layout, selectedPlotType, t, units),
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- figure.layout intentionally excluded (setFigure loop); consumptionSeries.updatedAt used as stable proxy
  }, [
    consumptionSeries.updatedAt,
    selectedGrouping,
    selectedProductGroup,
    selectedPlotType,
    selectedConsumptionSources,
    selectedMaterialGroups,
    dataLabelVisibility,
    t,
    units,
  ]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </div>
  );
};

const buildInitialFigData = (
  groupedConsumptionSeries: GroupedConsumptionSeries,
  dataLabelVisibility: DataLabelVisibility,
  t: TFunction
): Partial<PlotData>[] => {
  const materialColours = buildMaterialColours(
    groupedConsumptionSeries.items.map((item) => ({
      groupingName: item[0].materialGrouping,
      groupName: item[0].materialGroup,
    }))
  );

  const sortedSeries = sorted(groupedConsumptionSeries.items, ([key]) => [
    Object.keys(groupedConsumptionSeries.availableMaterialGroupings).findIndex(
      (materialGrouping) => materialGrouping === key.materialGrouping
    ),
    groupedConsumptionSeries.availableMaterialGroupings[
      key.materialGrouping
    ]?.findIndex((materialGroup) => materialGroup === key.materialGroup) ?? -1,
    groupedConsumptionSeries.availableConsumptionSources.findIndex(
      (consumptionSource) => consumptionSource === key.consumptionSource
    ),
    groupedConsumptionSeries.availableProductGroups.findIndex(
      (productGroup) => productGroup === key.productGroup
    ),
  ]);

  return sortedSeries.flatMap(([key, series]) => {
    const colour = getMaterialColour(
      materialColours,
      key.materialGrouping,
      key.materialGroup
    );
    const lineDash = getLineDash(
      groupedConsumptionSeries.availableConsumptionSources.indexOf(
        key.consumptionSource
      )
    );
    const markerSymbol = getMarkerSymbol(
      Array.from(groupedConsumptionSeries.availableConsumptionSources).indexOf(
        key.consumptionSource
      )
    );
    const buildDataLabelOptions = (texttemplate: string): Partial<PlotData> =>
      dataLabelVisibility === "shown"
        ? {
            mode: "text+lines+markers",
            texttemplate,
            textposition: "top center",
            textfont: { color: colour },
            cliponaxis: false,
          }
        : { mode: "lines+markers" };
    return [
      {
        x: series.date,
        y: series.consumed_mass,
        type: "scatter",
        ...buildDataLabelOptions("%{y:,.0f}"),
        visible: false,
        name: t(key.consumptionSource),
        legendgroup: key.materialGroup,
        legendgrouptitle: {
          text: t(key.materialGroup),
        },
        line: {
          color: colour,
          dash: lineDash,
        },
        marker: {
          color: colour,
          symbol: markerSymbol,
        },
        customdata: [
          key.materialGrouping,
          "mass",
          key.productGroup,
          key.productGrouping,
          key.consumptionSource,
          key.materialGroup,
        ],
      },
      {
        x: series.date,
        y: series.consumed_fraction,
        type: "scatter",
        ...buildDataLabelOptions(`%{y:${fractionTickFormatting}}`),
        visible: false,
        name: t(key.consumptionSource),
        legendgroup: key.materialGroup,
        legendgrouptitle: {
          text: t(key.materialGroup),
        },
        line: {
          color: colour,
          dash: lineDash,
        },
        marker: {
          color: colour,
          symbol: markerSymbol,
        },
        customdata: [
          key.materialGrouping,
          "fraction",
          key.productGroup,
          key.productGrouping,
          key.consumptionSource,
          key.materialGroup,
        ],
      },
    ];
  });
};

const updateFigData = (
  figData: Partial<PlotData>[],
  selectedGrouping: MaterialGroupingName | null,
  selectedProductGroup: string | null,
  selectedPlotType: ConsumptionPlotType,
  selectedConsumptionSources: ConsumptionSourceName[],
  selectedMaterialGroups: MaterialGroupName[]
): Partial<PlotData>[] => {
  return figData.map((trace) => ({
    ...trace,
    visible:
      trace.customdata?.[0] === selectedGrouping &&
      trace.customdata?.[1] === selectedPlotType &&
      selectedConsumptionSources.includes(
        trace.customdata?.[4] as ConsumptionSourceName
      ) &&
      selectedMaterialGroups.includes(
        trace.customdata?.[5] as MaterialGroupName
      ) &&
      (selectedProductGroup === "all"
        ? trace.customdata?.[3] === "all" // When "all" is selected, only show aggregated data
        : trace.customdata?.[2] === selectedProductGroup), // When specific group is selected, show that group's data
  }));
};

const updateLayout = (
  currentLayout: Partial<Layout>,
  selectedPlotType: ConsumptionPlotType,
  t: (key: string) => string,
  units: (key: UnitType) => string
): Partial<Layout> => {
  const yaxisTitle =
    selectedPlotType === "mass"
      ? `${t("consumption")}${units("mass")}`
      : `${t("consumption")} (%)`;

  return {
    ...currentLayout,
    yaxis: {
      ...currentLayout.yaxis,
      title: { text: yaxisTitle },
      tickformat:
        selectedPlotType === "fraction"
          ? fractionTickFormatting
          : generalTickFormatting,
    },
  };
};
